import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  createAcademicCalendarEvents,
  editAcademicCalendarEvents,
  deleteAcademicCalendarEvents,
  fetchAcademicCalendarEvents,
  setFormData,
  toggleModal,
  setEditing,
  setNotification,
  resetForm,
} from "../../store/academicCalendarFormSlice.js";
import "./CalendarForm.css";

const CalendarForm = ({
  schools,
  batches,
  terms,
  eventTypes,
  tableSectionRef,
}) => {
  const dispatch = useDispatch();
  const [validationErrors, setValidationErrors] = useState({});

  // Get all state from Redux store
  const {
    formData,
    isEditing,
    editingEventId,
    allEvents: existingEvents,
    showModal,
  } = useSelector((state) => state.academicCalendar);

  useEffect(() => {
    dispatch(fetchAcademicCalendarEvents());
  }, [dispatch]);

  const filteredBatches = useMemo(() => {
    if (formData.schoolId === "All" || !formData.schoolId) {
      return batches;
    }
    return batches.filter((batch) => batch.schoolId === formData.schoolId);
  }, [formData.schoolId, batches]);

  const isPublicEvent =
    formData.eventTypeID !== "All" &&
    eventTypes.find((et) => et.eventId === formData.eventTypeID)?.eventName ===
      "Public Events";

  useEffect(() => {
    if (!isEditing) {
      dispatch(resetForm());
    }
  }, [isEditing, dispatch]);

  const { startDate, endDate } = formData;
  useEffect(() => {
    if (startDate && !endDate) {
      dispatch(setFormData({ ...formData, endDate: startDate }));
    }
  }, [startDate, endDate, formData, dispatch]);

  const validateForm = () => {
    const errors = {};

    if (!formData.startDate) {
      errors.startDate = "Start date is required";
    }

    if (!formData.endDate) {
      errors.endDate = "End date is required";
    } else if (new Date(formData.endDate) < new Date(formData.startDate)) {
      errors.endDate = "End date must be on or after start date";
    }

    if (!formData.eventTypeID || formData.eventTypeID === "All") {
      errors.eventTypeID = "Event type is required";
    }
    console.log("1 validation formData.termId", formData.termId);
    console.log("1 validation formData.academicYear", formData.academicYear);
    console.log("1 validation formData", formData);

    if (!isPublicEvent) {
      if (!formData.termId && !formData.academicYear)
        errors.termId = "Term or academic year is required ";
    }
    setValidationErrors(errors);
    return Object.keys(errors).length === 0;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    let updatedFormData = { ...formData, [name]: value };

    if (name === "schoolId") {
      updatedFormData.batchId = "All";
    }

    if (isPublicEvent) {
      updatedFormData.schoolId = "All";
      updatedFormData.batchId = "All";
      updatedFormData.termId = "";
    }

    dispatch(setFormData(updatedFormData));

    // Clear validation error when field is edited
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({ ...prev, [name]: "" }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    console.log("before validation");
    if (!validateForm()) {
      dispatch(setNotification("Please fix the form errors"));
      return;
    }
    console.log("after validation");

    try {
      const eventData = {
        startDate: formData.startDate,
        endDate: formData.endDate,
        eventTypeId: formData.eventTypeID,
        termId: isPublicEvent ? null : formData.termId,
        academicYear: isPublicEvent ? null : formData.academicYear,
        schoolId: isPublicEvent ? null : formData.schoolId,
        batchId: isPublicEvent ? null : formData.batchId,
      };
      console.log("eventData", eventData);
      if (isEditing) {
        await dispatch(
          editAcademicCalendarEvents({
            eventId: editingEventId,
            event: eventData,
          })
        ).unwrap();
        dispatch(setNotification("Event updated successfully!"));
      } else {
        await dispatch(createAcademicCalendarEvents(eventData)).unwrap();
        dispatch(setNotification("Event added successfully!"));
      }

      dispatch(toggleModal(false));
      dispatch(resetForm());

      if (tableSectionRef.current) {
        tableSectionRef.current.scrollIntoView({ behavior: "smooth" });
      }
    } catch (error) {
      dispatch(setNotification(error.message || "Failed to save event"));
    }
  };

  const handleCancel = () => {
    dispatch(toggleModal(false));
    dispatch(resetForm());

    if (tableSectionRef.current) {
      tableSectionRef.current.scrollIntoView({ behavior: "smooth" });
    }
  };

  if (!showModal) return null;

  return (
    <div className="modal-overlay" role="dialog" aria-labelledby="modal-title">
      <div className="modal-content">
        <h2 id="modal-title">{isEditing ? "Edit Schedule" : "Add Schedule"}</h2>
        <form onSubmit={handleSubmit}>
          <div className="form-rows-container">
            {/* Row 1: Event Type and Short Title (two columns) */}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="eventTypeID">Event Type</label>
                <select
                  id="eventTypeID"
                  name="eventTypeID"
                  value={formData.eventTypeID}
                  onChange={handleChange}
                  required
                  className="full-visibility-select"
                >
                  <option value="All">Select Event Type</option>
                  {eventTypes.map((eventType) => (
                    <option
                      key={eventType.eventTypeID}
                      value={eventType.eventTypeID}
                    >
                      {eventType.eventName}-{eventType.eventDescription}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {/* Row 3: Start Date and End Date (two columns) */}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="startDate">Start Date</label>
                <input
                  type="date"
                  id="startDate"
                  name="startDate"
                  value={formData.startDate}
                  onChange={handleChange}
                  required
                />
              </div>

              <div className="form-group half-width">
                <label htmlFor="endDate">End Date</label>
                <input
                  type="date"
                  id="endDate"
                  name="endDate"
                  value={formData.endDate}
                  onChange={handleChange}
                  required
                />
              </div>
            </div>

            {/* Row 4: Term and Academic Year (two columns) */}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="termId">Term</label>
                <select
                  id="termId"
                  name="termId"
                  value={formData.termId}
                  onChange={handleChange}
                  disabled={isPublicEvent}
                  className="full-visibility-select"
                >
                  <option value="">Select Term</option>
                  {terms.map((term) => (
                    <option key={term.termId} value={term.termId}>
                      {term.name}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group half-width">
                <label htmlFor="academicYear">Academic Year</label>
                <select
                  id="academicYear"
                  name="academicYear"
                  value={formData.academicYear}
                  onChange={handleChange}
                  disabled={isPublicEvent}
                  className="full-visibility-select"
                >
                  <option value="">Select Academic Year</option>
                  {[...new Set(terms.map((term) => term.academicYear))].map(
                    (academicYear, index) => (
                      <option
                        key={`academic-year-${index}`}
                        value={academicYear}
                      >
                        {academicYear}
                      </option>
                    )
                  )}
                </select>
              </div>
            </div>

            {/* Row 5: School and Batch (two columns) */}
            <div className="form-row">
              <div className="form-group half-width">
                <label htmlFor="schoolId">School</label>
                <select
                  id="schoolId"
                  name="schoolId"
                  value={formData.schoolId}
                  onChange={handleChange}
                  required
                  disabled={isPublicEvent}
                  className="full-visibility-select"
                >
                  <option value="All">All</option>
                  {schools.map((school) => (
                    <option key={school.schoolId} value={school.schoolId}>
                      {school.schoolName}
                    </option>
                  ))}
                </select>
              </div>

              <div className="form-group half-width">
                <label htmlFor="batchId">Batch (in the school division)</label>
                <select
                  id="batchId"
                  name="batchId"
                  value={formData.batchId}
                  onChange={handleChange}
                  disabled={isPublicEvent}
                  className="full-visibility-select"
                >
                  <option value="All">All</option>
                  {filteredBatches.map((batch) => (
                    <option key={batch.batchId} value={batch.batchId}>
                      {batch.batchName}
                    </option>
                  ))}
                </select>
              </div>
            </div>
          </div>
          <div className="form-actions">
            <button type="submit" className="submit-btn">
              {isEditing ? "Update" : "Submit"}
            </button>
            <button type="button" className="cancel-btn" onClick={handleCancel}>
              Cancel
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default CalendarForm;
